import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { articles } from '../../mocks/articles';
import JsonLd from '../../components/base/JsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://workplaysave.com';
const DEFAULT_OG_IMAGE = 'https://public.readdy.ai/ai/img_res/7965406aa664502cb6a3888418e2a788.jpg';

const getCategoryColorClasses = (color: string) => {
  const colors: Record<string, { bg: string; text: string; border: string }> = {
    purple: { bg: 'bg-purple-600/20', text: 'text-purple-400', border: 'border-purple-500/30' },
    blue:   { bg: 'bg-cyan-600/20',   text: 'text-cyan-400',   border: 'border-cyan-500/30'   },
    green:  { bg: 'bg-emerald-600/20',text: 'text-emerald-400',border: 'border-emerald-500/30'},
  };
  return colors[color] ?? colors.purple;
};

export default function ArticlesPage() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = [
    { id: 'all', name: 'All Articles', color: 'from-purple-600 to-cyan-600' },
    { id: 'gaming', name: 'Gaming Tech', color: 'from-purple-600 to-purple-400' },
    { id: 'streaming', name: 'Streaming Equipment', color: 'from-cyan-600 to-cyan-400' },
    { id: 'office', name: 'Office Productivity', color: 'from-emerald-600 to-emerald-400' }
  ];

  const filteredArticles = selectedCategory === 'all'
    ? articles
    : articles.filter(article => {
        if (selectedCategory === 'gaming') return article.category === 'Gaming Tech';
        if (selectedCategory === 'streaming') return article.category === 'Streaming Equipment';
        if (selectedCategory === 'office') return article.category === 'Office Productivity';
        return true;
      });

  const collectionPageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Tech Articles | Gaming, Streaming, & Office Guides - Work Play Save',
    description: 'Explore in-depth tech guides, comparisons, and buying advice for gaming, streaming, and productivity setups. Learn what to buy and how to upgrade your gear.',
    url: `${SITE_URL}/articles`,
    publisher: {
      '@type': 'Organization',
      name: 'Work Play Save',
      url: SITE_URL,
    },
    hasPart: articles.map((a) => ({
      '@type': 'Article',
      name: a.title,
      url: `${SITE_URL}/article/${a.slug}`,
      datePublished: a.date,
      description: a.excerpt,
    })),
  }), []);

  return (
    <>
      <title>Tech Articles | Gaming, Streaming, &amp; Office Guides</title>
      <meta name="description" content="Explore in-depth tech guides, comparisons, and buying advice for gaming, streaming, and productivity setups. Learn what to buy and how to upgrade your gear." />
      <meta name="keywords" content="tech buying guides, gaming setup guides, streaming setup guides, productivity tech tips, gear comparisons, tech tutorials, gaming articles, streaming articles, tech advice" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${SITE_URL}/articles`} />
      <meta property="og:title" content="Tech Articles | Gaming, Streaming, &amp; Office Guides" />
      <meta property="og:description" content="Explore in-depth tech guides, comparisons, and buying advice for gaming, streaming, and productivity setups. Learn what to buy and how to upgrade your gear." />
      <meta property="og:url" content={`${SITE_URL}/articles`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:site_name" content="Work Play Save" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Tech Articles | Gaming, Streaming, &amp; Office Guides" />
      <meta name="twitter:description" content="Explore in-depth tech guides, comparisons, and buying advice for gaming, streaming, and productivity setups. Learn what to buy and how to upgrade your gear." />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      <JsonLd data={collectionPageSchema} />
      <div className="min-h-screen">
        {/* Page Hero */}
        <div className="relative bg-transparent border-b border-gray-800">
          <div className="max-w-7xl mx-auto px-6 py-12 relative z-10">
            <div className="text-center space-y-4">
              <h1 className="text-5xl font-bold text-white uppercase tracking-tight">
                All Articles
                <div className="h-1 w-32 bg-gradient-to-r from-transparent via-cyan-500 to-transparent mx-auto mt-4"></div>
              </h1>
              <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                Browse our complete collection of product reviews, buying guides, and tech recommendations.
              </p>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="relative bg-transparent border-b border-gray-800">
          <div className="max-w-7xl mx-auto px-6 py-6 relative z-10">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    selectedCategory === category.id
                      ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Articles Grid */}
        <div className="max-w-7xl mx-auto px-6 py-14 relative">
          <div className="relative z-10">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => {
                const cc = getCategoryColorClasses(article.categoryColor);
                return (
                  <Link key={article.id} to={`/article/${article.slug}`} className="group block">
                    <div className="bg-gray-900/30 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:border-purple-400/30 transition-all duration-400 h-full flex flex-col cursor-pointer">
                      {/* Thumbnail */}
                      <div className="relative h-56 w-full overflow-hidden flex-shrink-0">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/30 to-transparent"></div>
                        {/* Category badge */}
                        <div className="absolute top-4 left-4">
                          <span className={`inline-block px-3 py-1 ${cc.bg} ${cc.text} rounded-full text-xs font-semibold uppercase tracking-wider border ${cc.border} backdrop-blur-sm`}>
                            {article.category}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6 flex flex-col flex-1">
                        <p className="text-xs text-gray-500 mb-3">{article.date}</p>
                        <h3 className="text-lg font-bold text-white leading-snug group-hover:text-purple-300 transition-colors duration-300 mb-3 line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-sm text-gray-400 leading-relaxed flex-1 mb-5 line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-purple-400 group-hover:text-cyan-400 transition-colors duration-300 flex items-center gap-1.5 whitespace-nowrap">
                            Read Review
                            <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
                          </span>
                          <div className="flex items-center gap-3 text-xs text-gray-600">
                            <span className="flex items-center gap-1">
                              <i className="ri-time-line" />
                              {article.readTime}
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line" />
                              {article.views}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
